import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const filterAuditsSchema = z.object({
  auditable_type_contains: z.string().nullable().optional(),
  auditable_id_contains: z.string().nullable().optional(),
  user_id_contains: z.string().nullable().optional(),
  user_type_contains: z.string().nullable().optional(),
  username_contains: z.string().nullable().optional(),
  action_contains: z.string().nullable().optional(),
  version_contains: z.string().nullable().optional(),
  associated_type_contains: z.string().nullable().optional(),
  associated_id_contains: z.string().nullable().optional(),
  comment_contains: z.string().nullable().optional(),
  remote_address_contains: z.string().nullable().optional(),
  request_uuid_contains: z.string().nullable().optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterAuditsSchema = z.infer<typeof filterAuditsSchema>;

export const client = "qtl - admin";

export function FilterAudits() {
  const { t, i18n } = useTranslation();
  const fields: Field[] = useMemo(
    () => [
      {
        name: "auditable_type_contains",
        label: t("audits.columns.auditable_type", {
          ns: "tables",
          defaultValue: "Auditable type",
        }),
        type: "input",
      },
      {
        name: "auditable_id_contains",
        label: t("audits.columns.auditable_id", {
          ns: "tables",
          defaultValue: "Auditable ID",
        }),
        type: "input",
      },
      {
        name: "user_id_contains",
        label: t("audits.columns.user_id", {
          ns: "tables",
          defaultValue: "User ID",
        }),
        type: "input",
      },
      {
        name: "user_type_contains",
        label: t("audits.columns.user_type", {
          ns: "tables",
          defaultValue: "User type",
        }),
        type: "input",
      },
      {
        name: "username_contains",
        label: t("audits.columns.username", {
          ns: "tables",
          defaultValue: "Username",
        }),
        type: "input",
      },
      {
        name: "action_contains",
        label: t("audits.columns.action", {
          ns: "tables",
          defaultValue: "Action",
        }),
        type: "input",
      },
      {
        name: "version_contains",
        label: t("audits.columns.version", {
          ns: "tables",
          defaultValue: "Version",
        }),
        type: "input",
      },
      {
        name: "associated_type_contains",
        label: t("audits.columns.associated_type", {
          ns: "tables",
          defaultValue: "Associated type",
        }),
        type: "input",
      },
      {
        name: "associated_id_contains",
        label: t("audits.columns.associated_id", {
          ns: "tables",
          defaultValue: "Associated ID",
        }),
        type: "input",
      },
      {
        name: "comment_contains",
        label: t("audits.columns.comment", {
          ns: "tables",
          defaultValue: "Comment",
        }),
        type: "input",
      },
      {
        name: "remote_address_contains",
        label: t("audits.columns.remote_address", {
          ns: "tables",
          defaultValue: "Remote address",
        }),
        type: "input",
      },
      {
        name: "request_uuid_contains",
        label: t("audits.columns.request_uuid", {
          ns: "tables",
          defaultValue: "Request UUID",
        }),
        type: "input",
      },
      {
        name: "created_at_from",
        label: t("audits.columns.created_at_from", {
          ns: "tables",
          defaultValue: "Created from",
        }),
        type: "date",
      },
      {
        name: "created_at_to",
        label: t("audits.columns.created_at_to", {
          ns: "tables",
          defaultValue: "Created to",
        }),
        type: "date",
      },
    ],
    [t, i18n.language],
  );

  return (
    <FilterTable
      client={client}
      schema={filterAuditsSchema}
      tableName="audits"
      customBuildPath={() => `${client}/audits`}
      fields={fields}
      columns={2}
    />
  );
}
